import cx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerAddress } from '@interfaces/address.interface';
import {
  ActionIcon,
  Alert,
  Button,
  Divider,
  Grid,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';

import { useGetCartQuery } from '@api/cart.api';
import { useGetCurrentCustomerQuery } from '@api/customer.api';
import {
  useDeleteCustomerAddressMutation,
  useGetCustomerAddressesQuery,
} from '@api/customerAddress.api';
import { isApiError } from '@api/index';
import { useInsertOfferMutation } from '@api/offers.api';

import Card from '../card/Card';
import CartProductsTable from '../CartProductsTable';
import UpdateCustomerBillingAddress, {
  UPDATE_CUSTOMER_BILLING_ADDRESS_MODAL_ID,
} from '../UpdateCustomerBillingAddress';
import UpsertCustomerAddress, {
  UPSERT_CUSTOMER_ADDRESS_MODAL_ID,
} from '../UpsertCustomerAddress';
import classes from './Cart.module.css';

export default function Cart() {
  const { t } = useTranslation();

  // ==========================================================================
  // State
  // ==========================================================================
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(
    null,
  );

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data, isLoading } = useGetCartQuery();
  const [insertOffer, { isLoading: isLoadingInsertOffer }] =
    useInsertOfferMutation();

  const { data: customer, isLoading: isLoadingCustomer } =
    useGetCurrentCustomerQuery();
  const { data: addresses = [], isLoading: isLoadingAddresses } =
    useGetCustomerAddressesQuery();
  const [deleteCustomerAddress] = useDeleteCustomerAddressMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleEditProfileClick = () => {
    openModal({
      title: t('modals.editBillingAddress'),
      children: <UpdateCustomerBillingAddress customer={customer!} />,
      modalId: UPDATE_CUSTOMER_BILLING_ADDRESS_MODAL_ID,
    });
  };

  const handleAddAddressClick = () => {
    openModal({
      title: t('modals.addShippingAddress'),
      children: <UpsertCustomerAddress customer={customer} />,
      modalId: UPSERT_CUSTOMER_ADDRESS_MODAL_ID,
    });
  };

  const handleEditAddressClick = (address: CustomerAddress) => {
    openModal({
      title: t('modals.editShippingAddress'),
      children: (
        <UpsertCustomerAddress customerAddress={address} customer={customer} />
      ),
      modalId: UPSERT_CUSTOMER_ADDRESS_MODAL_ID,
    });
  };

  const handleDeleteAddressClick = async (id: number) => {
    openConfirmModal({
      title: t('cart.removeAddress'),
      children: t('cart.removeAddressConfirm'),
      labels: {
        cancel: t('cart.cancel'),
        confirm: t('cart.removeAddress'),
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteCustomerAddress(id).unwrap();

          if (selectedAddressId === id) {
            setSelectedAddressId(null);
          }

          showNotification({
            title: t('cart.addressRemoved'),
            message: t('cart.addressRemovedSuccess'),
          });
        } catch {
          showNotification({
            title: t('cart.error'),
            message: t('cart.cannotRemoveAddress'),
            color: 'red',
            icon: <IconX />,
          });
        }
      },
    });
  };

  const handleInsertOffer = async () => {
    try {
      await insertOffer({
        notes,
        customerAddressId: selectedAddressId!,
      }).unwrap();

      window.location.href = '/offer-success';
    } catch (error) {
      if (isApiError(error)) {
        setError(error.data.message);
      } else {
        setError(t('cart.generalError'));
      }
    }
  };

  // ==========================================================================
  // Memo
  // ==========================================================================
  const subtotal = useMemo(
    () =>
      data?.products.reduce(
        (acc, product) => acc + product.unitPrice * product.quantity,
        0,
      ) || 0,
    [data?.products],
  );

  const fees = useMemo(() => (subtotal <= 400 ? 60 : 40), [subtotal]);

  const taxes = useMemo(
    () => Number(((subtotal + fees) * 0.22).toFixed(2)),
    [subtotal, fees],
  );

  const total = useMemo(
    () => Number((subtotal + fees + taxes).toFixed(2)),
    [subtotal, fees, taxes],
  );

  // ==========================================================================
  // Render
  // ==========================================================================
  return isLoading ? (
    <Loader />
  ) : (
    <>
      <Title my="lg">{t('cart.title')}</Title>
      <Grid gutter="5rem">
        <Grid.Col span={{ sm: 12, md: 9 }}>
          {!data || data.products.length === 0 ? (
            <Text>{t('cart.noProducts')}</Text>
          ) : (
            <>
              <CartProductsTable products={data.products} />
              <Text my="xl" fw="600" fz="1.5rem" fs="italic">
                {t('cart.offerMessage')}
              </Text>

              <Title my="lg">{t('cart.addresses')}</Title>
              <Grid gutter="xl">
                <Grid.Col span={{ lg: 6 }}>
                  <Card
                    title={t('cart.billingAddress')}
                    action={{
                      title: t('cart.edit'),
                      icon: <IconPencil />,
                      onClick: handleEditProfileClick,
                    }}
                  >
                    {isLoadingCustomer ? (
                      <Loader />
                    ) : (
                      <>
                        <Text>
                          {customer?.user.firstName} {customer?.user.lastName}
                        </Text>
                        <Text>{customer?.address}</Text>
                        <Text>
                          {customer?.postalCode} - {customer?.city} (
                          {customer?.province})
                        </Text>
                      </>
                    )}
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ lg: 6 }}>
                  <Card
                    title={t('cart.shippingAddresses')}
                    action={
                      !isLoadingAddresses && addresses.length > 0
                        ? {
                            title: t('cart.add'),
                            icon: <IconPlus />,
                            onClick: handleAddAddressClick,
                          }
                        : undefined
                    }
                  >
                    {!isLoadingAddresses && addresses.length === 0 ? (
                      <>
                        <Text>{t('cart.noShippingAddress')}</Text>
                        <Button
                          onClick={handleAddAddressClick}
                          mt="md"
                          leftSection={<IconPlus />}
                        >
                          {t('cart.addShippingAddress')}
                        </Button>
                      </>
                    ) : isLoadingAddresses ? (
                      <Loader />
                    ) : (
                      <Stack>
                        {addresses.map((address) => (
                          <Group
                            key={address.id}
                            justify="space-between"
                            className={cx(
                              classes.shippingAddress,
                              address.id === selectedAddressId
                                ? classes.shippingAddressSelected
                                : undefined,
                            )}
                            onClick={() => setSelectedAddressId(address.id)}
                          >
                            {address.id === selectedAddressId && (
                              <IconCircleCheck
                                className={classes.shippingAddressSelectedIcon}
                              />
                            )}
                            <div>
                              <Text fw="bold">
                                {address.firstName} {address.lastName}
                              </Text>
                              <Text>{address.company}</Text>
                              <Text>{address.department}</Text>
                              <Text>{address.address}</Text>
                              <Text>
                                {address.postalCode} - {address.city} (
                                {address.province})
                              </Text>
                              {address.phone && address.phone.trim() !== '' && (
                                <Text>{address.phone}</Text>
                              )}
                            </div>
                            <Group gap="xs">
                              <Tooltip label={t('cart.edit')}>
                                <ActionIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditAddressClick(address);
                                  }}
                                >
                                  <IconPencil />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label={t('cart.delete')}>
                                <ActionIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAddressClick(address.id);
                                  }}
                                  color="red"
                                >
                                  <IconTrash />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </Card>
                </Grid.Col>
              </Grid>
            </>
          )}
        </Grid.Col>
        <Grid.Col span={{ sm: 12, md: 3 }}>
          {data && data.products.length > 0 && (
            <>
              <Stack gap="xs" flex="1 0 auto">
                <Title order={3} mb="lg">
                  {t('cart.costSummary')}
                </Title>
                <Group justify="space-between" align="end">
                  <Text>{t('cart.subtotal')}</Text>
                  <Text fw="bold" size="lg">
                    € {subtotal.toFixed(2)}
                  </Text>
                </Group>
                <Group justify="space-between" align="end" wrap="nowrap">
                  <Text>{t('cart.managementFees')}</Text>
                  <Text fw="bold" size="lg" ta="right">
                    € {fees.toFixed(2)}
                  </Text>
                </Group>
                <Group justify="space-between" align="end">
                  <Text>{t('cart.taxes')}</Text>
                  <Text fw="bold" size="lg">
                    € {taxes.toFixed(2)}
                  </Text>
                </Group>
                <Group justify="space-between" align="end" mt="md">
                  <Text size="lg" fs="italic">
                    {t('cart.total')}
                  </Text>
                  <Text fw="bold" size="lg">
                    € {total.toFixed(2)}
                  </Text>
                </Group>
              </Stack>

              <Divider my="xl" />

              <Textarea
                label={t('cart.notes')}
                placeholder={t('cart.notesPlaceholder')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                mb="lg"
              />

              {!selectedAddressId && (
                <Alert
                  maw="22rem"
                  mb="md"
                  variant="filled"
                  color="yellow"
                  title={t('cart.missingInfo')}
                  icon={<IconAlertTriangle />}
                >
                  {t('cart.selectShippingAddress')}
                </Alert>
              )}

              {error && (
                <Text c="red" size="sm" ta="center" mb="sm">
                  {error}
                </Text>
              )}

              <Group justify="flex-end">
                <Button
                  fullWidth
                  onClick={handleInsertOffer}
                  disabled={!selectedAddressId}
                  loading={isLoadingInsertOffer}
                >
                  {t('cart.requestOffer')}
                </Button>
              </Group>
            </>
          )}
        </Grid.Col>
      </Grid>
    </>
  );
}
